import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Surface, PageSection, formatINR } from "@/components/shared";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Search, Plus, FileText, ChevronLeft, ChevronRight, Printer, Ban, Pencil } from "lucide-react";
import { toast } from "sonner";

import { formatDate, formatDateTime } from "@/lib/datetime";
const STATUS_TONE = {
  draft: "bg-gray-100 text-gray-700 border-gray-200",
  approved: "bg-emerald-50 text-emerald-800 border-emerald-200",
  cancelled: "bg-red-50 text-red-800 border-red-200",
};

const PAGE_SIZE = 50;

export default function SalesInvoices() {
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [sourceFilter, setSourceFilter] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const load = useCallback(async () => {
    try {
      const params = { page, page_size: PAGE_SIZE };
      if (search) params.search = search;
      if (statusFilter) params.status = statusFilter;
      if (sourceFilter) params.source = sourceFilter;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const r = await api.get("/sales/invoices", { params });
      setItems(r.data.items || []);
      setTotal(r.data.total || 0);
      setTotalPages(r.data.total_pages || 1);
    } catch (e) { toast.error(e?.response?.data?.detail || "Load failed"); }
  }, [page, search, statusFilter, sourceFilter, dateFrom, dateTo]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [search, statusFilter, sourceFilter, dateFrom, dateTo]);

  const cancel = async (inv) => {
    if (!window.confirm(`Cancel invoice ${inv.invoice_no || "(draft)"}? Stock will be reversed if it was approved.`)) return;
    try { await api.post(`/sales/invoices/${inv.id}/cancel`); toast.success("Cancelled"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Cancel failed"); }
  };

  return (
    <div className="space-y-4">
      <PageSection
        title="Sales Invoices"
        description="Create GST-compliant tax invoices. Drafts can be edited freely; approved invoices deduct warehouse stock and lock the invoice number."
        action={
          <Button className="bg-[#9B1C1C] hover:bg-[#7F1D1D] gap-1.5" onClick={() => nav("/sales/invoices/new")} data-testid="inv-new-btn">
            <Plus className="h-4 w-4" /> New Invoice
          </Button>
        }
      >
        <Surface padded={false}>
          <div className="p-3 border-b border-gray-100 flex items-center gap-2 flex-wrap">
            <div className="relative flex-1 max-w-xs min-w-[220px]">
              <Search className="h-4 w-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
              <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Invoice # / customer / reference..." className="pl-8" data-testid="inv-search" />
            </div>
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="h-9 rounded-md border border-gray-200 text-sm px-2" data-testid="inv-status-filter">
              <option value="">All status</option>
              <option value="draft">Draft</option>
              <option value="approved">Approved</option>
              <option value="cancelled">Cancelled</option>
            </select>
            <select value={sourceFilter} onChange={(e) => setSourceFilter(e.target.value)} className="h-9 rounded-md border border-gray-200 text-sm px-2" data-testid="inv-source-filter">
              <option value="">All sources</option>
              <option value="hq_direct">HQ Direct</option>
              <option value="branch">Branch</option>
              <option value="franchise">Franchise</option>
              <option value="online">Online</option>
            </select>
            <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="h-9 w-40" data-testid="inv-date-from" title="From date" />
            <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="h-9 w-40" data-testid="inv-date-to" title="To date" />
            <span className="text-xs text-gray-500 ml-auto">{total.toLocaleString()} invoice(s)</span>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr className="text-left text-[11px] uppercase tracking-widest text-gray-500">
                <th className="px-3 py-2">Invoice #</th>
                <th className="px-3 py-2">Date</th>
                <th className="px-3 py-2">Customer</th>
                <th className="px-3 py-2">Warehouse</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2 text-right">Taxable</th>
                <th className="px-3 py-2 text-right">Tax</th>
                <th className="px-3 py-2 text-right">Grand Total</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {items.length === 0 && (
                <tr><td colSpan={9} className="px-3 py-10 text-center text-gray-400">
                  <FileText className="h-8 w-8 mx-auto mb-2 opacity-30" /> No invoices.
                </td></tr>
              )}
              {items.map((inv) => (
                <tr key={inv.id} className="hover:bg-gray-50" data-testid={`inv-row-${inv.id}`}>
                  <td className="px-3 py-2 font-mono text-xs font-semibold">{inv.invoice_no || <span className="text-gray-400">(draft)</span>}</td>
                  <td className="px-3 py-2 text-xs">{formatDate(inv.invoice_date)}</td>
                  <td className="px-3 py-2">
                    <div className="font-medium">{inv.customer_snapshot?.name || "—"}</div>
                    <div className="text-[11px] text-gray-500 font-mono">{inv.customer_snapshot?.code || ""}</div>
                  </td>
                  <td className="px-3 py-2 text-xs text-gray-700">{inv.warehouse_name || "—"}</td>
                  <td className="px-3 py-2">
                    <span className={`text-[10px] uppercase tracking-widest font-semibold border rounded px-2 py-0.5 ${STATUS_TONE[inv.status] || ""}`}>{inv.status}</span>
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums text-xs">{formatINR(inv.totals?.taxable_amount || 0)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-xs">{formatINR(inv.totals?.tax_total || 0)}</td>
                  <td className="px-3 py-2 text-right tabular-nums font-semibold">{formatINR(inv.totals?.grand_total || 0)}</td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    {inv.status === "draft" && (
                      <button className="h-7 w-7 rounded hover:bg-gray-100 inline-flex items-center justify-center" onClick={() => nav(`/sales/invoices/${inv.id}/edit`)} title="Edit" data-testid={`inv-edit-${inv.id}`}>
                        <Pencil className="h-3.5 w-3.5 text-gray-500" />
                      </button>
                    )}
                    {inv.status === "approved" && (
                      <a className="h-7 w-7 rounded hover:bg-gray-100 inline-flex items-center justify-center" href={`/sales/invoices/${inv.id}/print`} target="_blank" rel="noreferrer" title="Print" data-testid={`inv-print-${inv.id}`}>
                        <Printer className="h-3.5 w-3.5 text-gray-500" />
                      </a>
                    )}
                    {inv.status !== "cancelled" && (
                      <button className="h-7 w-7 rounded hover:bg-red-50 inline-flex items-center justify-center ml-1" onClick={() => cancel(inv)} title="Cancel" data-testid={`inv-cancel-${inv.id}`}>
                        <Ban className="h-3.5 w-3.5 text-red-500" />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Surface>
        {totalPages > 1 && (
          <div className="mt-3 bg-white border border-gray-200 rounded-md px-4 py-2.5 flex items-center gap-2 text-xs" data-testid="inv-pagination">
            <span>Page <span className="font-semibold">{page}</span> of {totalPages}</span>
            <div className="ml-auto flex gap-1">
              <Button size="sm" variant="outline" className="h-8 px-2" disabled={page <= 1} onClick={() => setPage(1)}>« First</Button>
              <Button size="sm" variant="outline" className="h-8 px-2" disabled={page <= 1} onClick={() => setPage(page - 1)}><ChevronLeft className="h-4 w-4" /></Button>
              <Button size="sm" variant="outline" className="h-8 px-2" disabled={page >= totalPages} onClick={() => setPage(page + 1)}><ChevronRight className="h-4 w-4" /></Button>
              <Button size="sm" variant="outline" className="h-8 px-2" disabled={page >= totalPages} onClick={() => setPage(totalPages)}>Last »</Button>
            </div>
          </div>
        )}
      </PageSection>
    </div>
  );
}
